import { ReactNode } from "react";
import { cn } from "@/utils/cn";
import { SearchIcon } from "@/components/icons";

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterSelect {
  key: string;
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  /** Tailwind width class, e.g. "w-40". */
  width?: string;
}

interface FilterBarProps {
  selects: FilterSelect[];
  search?: { value: string; onChange: (value: string) => void; placeholder: string };
  /** Right-aligned extras, e.g. a one-line caption. */
  trailing?: ReactNode;
}

/**
 * One explicit height for the selects and the search box, with no vertical padding: a select's natural height falls
 * short of a text input's, so a bar of selects only would sit lower than one with search, and the table under it would
 * move on a tab change (DD-042).
 */
const controlHeight = "h-8";
const selectClass = cn(controlHeight, "text-sm border border-gray-300 rounded-lg px-3 bg-white");

/** The filter row of a table page (DD-042): plain selects and a search box, state kept by the caller in the URL. */
export function FilterBar({ selects, search, trailing }: FilterBarProps) {
  return (
    // min-h-14 is py-3 plus a control: a bar with no control (Prices, a caption only) still starts the table where the others do (DD-042).
    <div className="shrink-0 min-h-14 bg-white rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      {selects.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 min-w-0">
          {selects.map((s) => (
            <select key={s.key} aria-label={s.label} value={s.value} onChange={(e) => s.onChange(e.target.value)} className={cn(selectClass, s.width ?? "w-40")}>
              {s.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ))}
        </div>
      )}
      {/* Without a caption the group keeps its width. With one, it takes its own line above the search box on a phone and wraps beside it from sm up. */}
      <div className={cn("flex items-center gap-3 sm:ml-auto", trailing ? "flex-wrap sm:flex-nowrap sm:min-w-0" : "sm:shrink-0")}>
        {trailing}
        {search && (
          <div className="relative w-full sm:w-64 sm:shrink-0">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder={search.placeholder}
              aria-label={search.placeholder}
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              className={cn(controlHeight, "w-full pl-9 pr-4 text-sm border border-gray-300 rounded-lg")}
            />
          </div>
        )}
      </div>
    </div>
  );
}
